// ── One continuous SVG path through a step series ────────────────────────────
//
// The probability plot draws a piecewise-constant trace: a horizontal per vintage,
// joined by a rounded shoulder where the level changes. A single-colour line draws
// it as ONE path rather than a subpath per piece — with round caps every abutment
// grew a ~1px nub, and at a daily cadence zoomed out (segments only a few px wide)
// the fixed shoulder overran its neighbours outright.
//
// Lifted out of <ProbabilityPlot> because the invariant that matters here is a
// property of the emitted `d` string — a step's vertical must never extend past the
// horizontal it meets — and it only breaks in corners that are awkward to reach
// through the component: sub-pixel vintage spacing, and a trace clamped (by a
// resolution or a snapshot rewind) a moment after its last vintage. Both are one
// assertion away when the builder is pure.

/**
 * Round to the tenth of a pixel the `d` string carries.
 *
 * Load-bearing, not cosmetic: the room checks below must decide on the numbers
 * ACTUALLY EMITTED. Deciding at full precision let a shoulder of 0.03px print as
 * `0.0`, which turned the shoulder curve into a hard vertical whose control points
 * had collapsed onto its endpoints — and a round cap on that end then extended the
 * vertical past the horizontal it was supposed to meet.
 */
const r1 = (n) => Math.round(n * 10) / 10;

/**
 * Fold away each span too narrow to show its level when the line reverses across it.
 *
 * Two vintages a moment apart that disagree (one update recorded at its click and again
 * at the server's stamp, two runs landing in the same hour) leave a span a pixel or two
 * wide. A step series draws it as up-and-straight-back-down in one column: a spike that
 * reads as a rendering fault rather than as a forecast. Such a span is merged into the one
 * after it, so the line steps once, from the level before the burst to the level after.
 *
 * Only reversals fold: a narrow span between a lower and a higher level sits inside that
 * step anyway, so a tight monotone run keeps its staircase. Adjacent equal levels merge
 * too, since they are one horizontal and would otherwise hide a reversal across them. The
 * first and last spans are never folded, so the line still starts at generation and ends
 * on the live value. The data is untouched: zoomed in, the span is wide enough to draw.
 *
 * @param {Array<{startMs:number, endMs:number, p:number}>} checkpoints
 * @param {(ms:number) => number} xAtMs
 * @param {number} minSpanPx a reversal narrower than this is folded
 * @returns {Array<{startMs:number, endMs:number, p:number}>}
 */
export function collapseNarrowReversals(checkpoints, xAtMs, minSpanPx) {
  const out = [];
  for (const cp of checkpoints) {
    let cur = cp;
    for (;;) {
      const last = out[out.length - 1];
      if (!last) break;
      if (last.p !== cur.p) {
        const prev = out[out.length - 2];
        if (!prev) break;
        const narrow = xAtMs(last.endMs) - xAtMs(last.startMs) < minSpanPx;
        const reverses = (last.p - prev.p) * (cur.p - last.p) < 0;
        if (!narrow || !reverses) break;
      }
      out.pop();
      cur = { ...cur, startMs: last.startMs };
    }
    out.push(cur);
  }
  return out;
}

/**
 * Build the continuous step path for one outcome's checkpoints.
 *
 * Emits only three shapes — a horizontal `L`, a shoulder `C` (monotone in x and y,
 * so it stays inside the two levels it joins), and, where there is no room for a
 * shoulder, a hard vertical `L`. Never a zero-length command and never a cubic with
 * a collapsed control point: those have no direction of their own, so the renderer
 * infers the cap/join tangent from the neighbouring VERTICAL and the stroke then
 * pokes out past the level. The caller pairs this with a butt linecap and a miter
 * linejoin, which together keep the stroke inside the geometry (see ProbabilityPlot).
 *
 * @param {{
 *   checkpoints: Array<{startMs:number, endMs:number, p:number}>, // constant-p spans
 *   xAtMs: (ms:number) => number,
 *   yAtProb: (p:number) => number,
 *   vw: number,            // viewport width in px — spans wholly outside it are dropped
 *   shoulder?: number,     // half-width of a full-size shoulder, in px
 * }} args
 * @returns {{ d: string, segs: Array<{i:number, cp:object, y:number, xStart:number,
 *   xEnd:number, xSegStart:number, xSegEnd:number}> }}
 */
export function buildStepPath({ checkpoints: raw, xAtMs, yAtProb, vw, shoulder = 2 }) {
  // A span that cannot hold a full shoulder at each end is the width a spike is drawn at.
  const checkpoints = collapseNarrowReversals(raw, xAtMs, 2 * shoulder);
  let d = "";
  let open = false;
  // The last point the string actually names. It is also where the next span starts:
  // the path is continuous, so a continuation needs no start coordinate of its own —
  // which is what makes "no gap, no backwards step" structural rather than arithmetic.
  let curX = 0;
  const segs = [];
  for (let i = 0; i < checkpoints.length; i++) {
    const cp = checkpoints[i];
    const next = checkpoints[i + 1];
    const y = r1(yAtProb(cp.p));
    const xStart = xAtMs(cp.startMs);
    const xEnd = xAtMs(cp.endMs);
    if (xEnd < -20 || xStart > vw + 20) {
      // Off-screen: end the run so the next visible segment starts a fresh `M`
      // rather than drawing a line straight across the gap.
      open = false;
      continue;
    }
    // Never let a shoulder eat more than half of either neighbouring span: the next
    // span's own left inset is this same `half`, so halving both sides is what keeps
    // consecutive shoulders from crossing.
    const nextEnd = next ? xAtMs(next.endMs) : xEnd;
    const half = next
      ? Math.max(0, Math.min(shoulder, (xEnd - xStart) / 2, (nextEnd - xEnd) / 2))
      : 0;
    const xStep = r1(xEnd);
    const xShoulderIn = r1(xEnd - half);
    const xShoulderOut = r1(xEnd + half);
    const yNext = next ? r1(yAtProb(next.p)) : null;
    // A vintage that repeats the previous level (or rounds onto it) is not a step: the
    // horizontal simply carries on, needing neither a joint nor room for one. Stepping
    // anyway would emit a zero-length command — nothing for a cap or join to take a
    // direction from — and an outcome sitting still is the common case, not a corner.
    const steps = yNext !== null && yNext !== y;
    // A shoulder exists only if it prints on BOTH sides of the step. Testing `half`
    // itself is not enough: a sub-pixel shoulder rounds onto the step's own x, and the
    // curve then has a control point sitting on one of its endpoints — a "curve" with
    // no direction at that end, which the renderer caps/joins along the VERTICAL
    // instead, leaving a spur past the level.
    const hasShoulder = steps && xShoulderIn < xStep && xStep < xShoulderOut;
    const xTo = hasShoulder ? xShoulderIn : xStep;
    if (!open) {
      curX = r1(xStart);
      d += ` M ${curX} ${y}`;
      open = true;
    }
    const xSegStart = curX;
    if (xTo > curX) {
      d += ` L ${xTo} ${y}`;
      curX = xTo;
    }
    if (steps) {
      if (hasShoulder) {
        d += ` C ${xStep} ${y} ${xStep} ${yNext} ${xShoulderOut} ${yNext}`;
        curX = xShoulderOut;
      } else {
        // A hard step, and exactly vertical: `curX` is the step's x whenever the
        // horizontal was drawn, and where it was too short to draw, holding curX is
        // what keeps the step from stepping backwards over its neighbour.
        d += ` L ${curX} ${yNext}`;
      }
    }
    segs.push({ i, cp, y, xStart, xEnd, xSegStart, xSegEnd: xTo });
  }
  return { d: d.trim(), segs };
}
